import type { MeasurementOverlayRecord } from "./measurement-overlay-types";

/** True when this measurement owns the shared group label. */
export function isLabelGroupAnchor(measurement: MeasurementOverlayRecord): boolean {
  return !!measurement.labelGroupId && String(measurement._id) === measurement.labelGroupId;
}

/** True when this measurement is in a group but does not own the label. */
export function isLabelGroupMember(measurement: MeasurementOverlayRecord): boolean {
  return !!measurement.labelGroupId && String(measurement._id) !== measurement.labelGroupId;
}

/** Hide values on active shared-label groups (one label, no quantity line). */
export function shouldShowMeasurementValue(
  showValues: boolean | undefined,
  inActiveLabelGroup: boolean
): boolean {
  if (showValues === false) return false;
  return !inActiveLabelGroup;
}

/**
 * Build labelGroupId → member records for the current page overlay.
 * Only includes active groups: 2+ members present **and** the anchor record
 * (`_id === labelGroupId`) is among them. Without an anchor, members render as
 * ungrouped so filtered/orphan views never hide every label.
 */
export function buildLabelGroupMembersById(
  measurements: MeasurementOverlayRecord[]
): Map<string, MeasurementOverlayRecord[]> {
  const byGroup = new Map<string, MeasurementOverlayRecord[]>();
  for (const measurement of measurements) {
    if (!measurement.labelGroupId) continue;
    const list = byGroup.get(measurement.labelGroupId) ?? [];
    list.push(measurement);
    byGroup.set(measurement.labelGroupId, list);
  }
  for (const [groupId, members] of byGroup) {
    const hasAnchor = members.some((member) => String(member._id) === groupId);
    if (members.length < 2 || !hasAnchor) {
      byGroup.delete(groupId);
    }
  }
  return byGroup;
}

export function canGroupSelectedMeasurements(
  selected: MeasurementOverlayRecord[]
): { ok: true } | { ok: false; reason: string } {
  if (selected.length < 2) {
    return { ok: false, reason: "Select at least two measurements" };
  }
  const scopeKeys = new Set(
    selected.map(
      (item) => item.labelGroupScopeKey ?? (item.tagId ? String(item.tagId) : "")
    )
  );
  if (scopeKeys.size > 1) {
    return {
      ok: false,
      reason: "Measurements must share the same tag and sub-task",
    };
  }
  return { ok: true };
}

export function selectionLabelGroupState(selected: MeasurementOverlayRecord[]): {
  allGrouped: boolean;
  anyGrouped: boolean;
  sharedGroupId: string | null;
} {
  const groupIds = selected.map((item) => item.labelGroupId ?? null);
  const anyGrouped = groupIds.some((id) => !!id);
  const allGrouped = selected.length > 0 && groupIds.every((id) => !!id);
  const unique = new Set(groupIds.filter((id): id is string => !!id));
  const sharedGroupId = unique.size === 1 ? [...unique][0]! : null;
  return { allGrouped, anyGrouped, sharedGroupId };
}
